import React, {useCallback, useEffect, useState} from 'react';
import {Button} from '@mui/material';
import {
    fetchSignInMethods, methodPageUrl,
    type MethodPageAction, type SecondFactorMethod, type SignInMethods,
} from '../../Auth/signInMethods';

type SignInSecuritySectionProps = {
    load?: () => Promise<SignInMethods>;
    /** Open Outlaw Sign-in's page; it returns to the URL given. */
    open?: (url: string) => void;
};

const FACTOR_KINDS: Record<SecondFactorMethod['kind'], string> = {
    totp: 'Authenticator app',
    email: 'Email code',
};

const formatDate = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
};

const formatDateTime = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'});
};

// A new tab, so unsaved work in this one (My Profile edits, open forms) stays as it is.
const openPage = (url: string) => { window.open(url, '_blank', 'noopener'); };

/** The "Sign-in and security" section of My Profile (OWA-1191). Every change happens on Outlaw Sign-in. */
export default function SignInSecuritySection({load = fetchSignInMethods, open = openPage}: SignInSecuritySectionProps) {
    const [state, setState] = useState<{status: 'loading'} | {status: 'error'} | {status: 'ready'; methods: SignInMethods}>({status: 'loading'});

    const refresh = useCallback(() => {
        let live = true;
        setState({status: 'loading'});
        load().then(
            methods => { if (live) setState({status: 'ready', methods}); },
            () => { if (live) setState({status: 'error'}); },
        );
        return () => { live = false; };
    }, [load]);

    useEffect(() => refresh(), [refresh]);

    const go = (action: MethodPageAction) => open(methodPageUrl(action, window.location.href));

    return (
        <section className="my-profile-dialog__security" aria-labelledby="my-profile-security-title">
            <div className="my-profile-dialog__section-divider">
                <span id="my-profile-security-title">Sign-in and security</span>
            </div>

            {state.status === 'loading' && <p className="my-profile-dialog__security-note" role="status">Loading sign-in methods</p>}

            {state.status === 'error' && (
                <div className="my-profile-dialog__security-note" role="alert">
                    <p>Could not load your sign-in methods.</p>
                    <Button variant="outlined" size="small" onClick={refresh}>Try again</Button>
                </div>
            )}

            {state.status === 'ready' && (
                <>
                    <h3 className="my-profile-dialog__security-heading">Passkeys</h3>
                    {state.methods.passkeys.length === 0
                        ? <p className="my-profile-dialog__security-note">No passkeys yet.</p>
                        : <ul className="my-profile-dialog__security-list" aria-label="Passkeys">
                            {state.methods.passkeys.map(passkey => (
                                <li key={passkey.id} className="my-profile-dialog__security-row">
                                    <span className="my-profile-dialog__security-text">
                                        <span className="my-profile-dialog__security-name">{passkey.name}</span>
                                        <span className="my-profile-dialog__security-detail">Added {formatDate(passkey.created_at)}</span>
                                    </span>
                                    <Button variant="text" size="small" color="warning" aria-label={`Remove passkey ${passkey.name}`}
                                            onClick={() => go({page: 'passkey/remove', id: passkey.id})}>Remove</Button>
                                </li>
                            ))}
                        </ul>}
                    <div className="my-profile-dialog__button-row">
                        <Button variant="outlined" size="small" onClick={() => go({page: 'passkey/add'})}>Add a passkey</Button>
                    </div>

                    {/* Staff are passkey-only; Outlaw Sign-in says who may use a password. */}
                    {state.methods.password_allowed === true && (
                        <>
                            <h3 className="my-profile-dialog__security-heading">Second factors</h3>
                            {state.methods.second_factors.length === 0
                                ? <p className="my-profile-dialog__security-note">No second factor yet.</p>
                                : <ul className="my-profile-dialog__security-list" aria-label="Second factors">
                                    {state.methods.second_factors.map(factor => (
                                        <li key={factor.id} className="my-profile-dialog__security-row">
                                            <span className="my-profile-dialog__security-text">
                                                <span className="my-profile-dialog__security-name">{FACTOR_KINDS[factor.kind]}</span>
                                                <span className="my-profile-dialog__security-detail">{factor.label} {factor.detail}</span>
                                            </span>
                                            <Button variant="text" size="small" color="warning"
                                                    aria-label={`Remove ${factor.label}`}
                                                    onClick={() => go({page: 'factor/remove', id: factor.id})}>Remove</Button>
                                        </li>
                                    ))}
                                </ul>}
                            <div className="my-profile-dialog__button-row">
                                <Button variant="outlined" size="small" onClick={() => go({page: 'factor/add', kind: 'totp'})}>Add an authenticator app</Button>
                                <Button variant="outlined" size="small" onClick={() => go({page: 'factor/add', kind: 'email'})}>Add an email code</Button>
                                <Button variant="outlined" size="small" onClick={() => go({page: 'password'})}>Change password</Button>
                            </div>
                        </>
                    )}

                    <h3 className="my-profile-dialog__security-heading">Signed-in devices</h3>
                    {state.methods.devices.length === 0
                        ? <p className="my-profile-dialog__security-note">No other devices are signed in.</p>
                        : <ul className="my-profile-dialog__security-list" aria-label="Signed-in devices">
                            {state.methods.devices.map(device => (
                                <li key={device.id} className="my-profile-dialog__security-row">
                                    <span className="my-profile-dialog__security-text">
                                        <span className="my-profile-dialog__security-name">
                                            {device.label}{device.current && <strong className="my-profile-dialog__security-current"> This device</strong>}
                                        </span>
                                        <span className="my-profile-dialog__security-detail">
                                            {device.location} · Last seen {formatDateTime(device.last_seen_at)}
                                        </span>
                                    </span>
                                    {!device.current && (
                                        <Button variant="text" size="small" color="warning" aria-label={`Sign out ${device.label}`}
                                                onClick={() => go({page: 'device/sign-out', id: device.id})}>Sign out</Button>
                                    )}
                                </li>
                            ))}
                        </ul>}
                </>
            )}
        </section>
    );
}
